import {
  AccountCategory,
  CalculatedCostSell,
  CalculatedCostState,
  CashInfo,
  Currency,
  deepEqual,
  getCorrectExchangeRate,
  getFormatter,
  InstrumentStoreItem,
  isDefined,
  Loadable,
  LoadableState,
  LoadableStateLoaded,
  NbFormatter,
  OrderType,
  SelectedAccountOrClient,
  TradeType,
  useDebounce,
  useLoadableState,
  useLoadableStateTransition,
} from '@eih-netbroker/common';
import { Fade } from '@mui/material';
import BigNumber from 'bignumber.js';
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { ReactComponent as NoCurrencyExchangeIcon } from '../../../../assets/icons/no_exchange.svg';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { NbButton } from '../../../../shared/components/button/Button';
import { FeedbackBox } from '../../../../shared/components/feedback-box/FeedbackBox';
import { NbIndefiniteLoader } from '../../../../shared/components/loader/Loader';
import { NbSwitch } from '../../../../shared/components/toggle-button/Switch';
import { useInventory } from '../../../../shared/services/inventory/useInventory';
import { useAccountBalance } from '../../../../shared/services/useAccountBalance';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { useSourceAndTargetCurrencies } from '../../../../shared/services/useSourceAndTargetCurrencies';
import { cssWhen } from '../../../../shared/utils/themeUtils';
import { CurrencyDominance } from '../../../core/components/currency-exchange/CurrencyExchangeContext';
import { CurrencyExchangeNotAvailableModal } from '../../../core/components/currency-exchange/CurrencyExchangeNotAvailableModal';
import { ValidatedOrder } from '../../services/order/useNormalOrderView';
import { useOrderConfirmCalculateCost } from '../../services/order/useOrderConfirmCalculateCost';
import { useOrderCurrencyExchange } from '../../services/order/useOrderCurrencyExchange';
import { useOrderExchange } from '../../services/order/useOrderExchange';
import { useOrderManagement } from '../../services/order/useOrderManagement';
import { OrderCurrencyExchangeSourceAndTargetCurrencyManagerContext } from '../OrderCurrencyExchangeSourceAndTargetCurrencyManagerProvider';
import { OrderExchangeValidationError, useOrderCurrencyExchangeState } from '../OrderCurrencyExchangeStateContext';
import { useOrderState } from '../OrderStateContext';
import classes from './OrderExchange.module.scss';
import { OrderCurrencyExchange } from './OrderExchangePartials';
import { OrderCurrencyExchangeParams, OrderFormData } from './OrderForm';

type OrderExchangeProps = {
  isExchangeEnabled: boolean;
  setIsExchangeEnabled: (val: boolean) => void;
  order?: OrderFormData;
  instrumentData: InstrumentStoreItem | null;
  updateOrder: (value?: OrderCurrencyExchangeParams) => void;
  initialOrder: ValidatedOrder | null; // When modifying an existing order, we need to pass the initial order to the component
};

const CurrencyWhenLoaded = <T,>({
  value,
  getValue,
  currency,
  formatCurrency,
}: {
  value: LoadableState<T>;
  getValue?: (value: LoadableStateLoaded<T>) => number;
  currency: string;
  formatCurrency: typeof NbFormatter.prototype.formatCurrency;
}) => {
  if ((!value.hasError && !value.isLoading && !value.isLoaded) || value.hasError) {
    return <p>-</p>;
  } else if (!value.isLoaded) {
    return (
      <NbIndefiniteLoader
        className={classes.loader}
        isContrast={true}
      />
    );
  } else {
    return (
      <p>
        {getValue
          ? formatCurrency(getValue(value), currency)
          : formatCurrency((value as unknown as LoadableStateLoaded<number>).value, currency)}
      </p>
    );
  }
};

export const OrderExchange = ({
  isExchangeEnabled,
  setIsExchangeEnabled,
  order,
  updateOrder,
  initialOrder,
}: OrderExchangeProps) => {
  const { translation, translateWithParams } = useLocale();
  const { formatCurrency, formatDate, formatNumber } = getFormatter();
  const { calculateOrderCost } = useOrderConfirmCalculateCost();
  const [isFullTransactionChange, setIsFullTransactionChange] = useState<boolean>(false);
  const [localOrder, setLocalOrder] = useState<OrderFormData>();
  const { debounce } = useDebounce(() => {}, 250);
  const { debounce: exchangeRateDebounce } = useDebounce(() => {}, 250);

  const { loadSourceCurrencies, startRefreshCurrencies, stopRefreshCurrencies, resetLoadables } =
    useSourceAndTargetCurrencies();
  const { currencyExchangeBalance } = useOrderCurrencyExchange();
  const { orderCurrencyExchangeSourceCurrencies: sourceCurrencies } = useContext(
    OrderCurrencyExchangeSourceAndTargetCurrencyManagerContext,
  );
  const { availableBalance } = useOrderState();
  const {
    selectedSource,
    setCurrencyExchangeBalance,
    setExchangeRate,
    setExchangeSettlementDate,
    setTargetAmount,
    setSelectedSource,
    setSourceAmount,
    exchangeRate,
    sourceAmount,
    exchangeSettlementDate,
    targetAmount,
    pushValidationError,
    removeValidationError,
    hasError,
    baseInfo,
    setBaseInfo,
  } = useOrderCurrencyExchangeState();
  const { getCashInfoByCurrency } = useAccountBalance();
  const { selectedAccountOrClient } = useAccounts();
  const selectedAccountNumber = selectedAccountOrClient.account?.accountNumber;
  const { getExchangeRateForOrder, loadExchangeRateForOrder } = useOrderCurrencyExchange();
  const { startTransition, isPending } = useLoadableStateTransition();
  const { inventoryDetails } = useInventory();
  const { getIsValidOrder } = useOrderExchange();
  const { costCalculation, mapOrderFormToOrderRequest } = useOrderManagement();
  const { transactionId } = useOrderState();
  const [initialCalculatedCost, setInitialCalculatedCost] = useState<LoadableState<number>>(Loadable.uninitialized);

  const calculateInitialCalculatedCost = useCallback(async () => {
    if (initialOrder && initialOrder.tradeType === TradeType.Buy) {
      try {
        setInitialCalculatedCost(Loadable.loading);
        const orderCost = (await costCalculation(
          mapOrderFormToOrderRequest(initialOrder),
          transactionId,
        )) as CalculatedCostSell;
        setInitialCalculatedCost(Loadable.loaded(orderCost.buyAmount));
      } catch (e) {
        setTimeout(() => {
          calculateInitialCalculatedCost();
        }, 5000);
      }
    } else {
      setInitialCalculatedCost(Loadable.loaded(0));
    }
  }, [costCalculation, initialOrder, mapOrderFormToOrderRequest, transactionId]);

  useLayoutEffect(() => {
    calculateInitialCalculatedCost();

    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, []);

  const initialOrderPrice = useMemo(() => {
    if (initialOrder && initialOrder.price && initialOrder.quantity) {
      return new BigNumber(initialOrder.price).times(initialOrder.quantity).toNumber();
    }
  }, [initialOrder]);

  const displayOrderExchange = useMemo(() => {
    if (isExchangeEnabled) {
      return true;
    }
    const isValidOrder = getIsValidOrder(order);
    if (!sourceCurrencies.isLoaded || !isValidOrder || isDefined(selectedAccountOrClient.client)) {
      return false;
    } else if (
      (order?.orderType === OrderType.Limit ||
        order?.orderType === OrderType.StopLimit ||
        order?.orderType === OrderType.Oco) &&
      sourceCurrencies.isLoaded &&
      sourceCurrencies.length > 0 &&
      selectedAccountOrClient.account?.accountCategory !== AccountCategory.Nyesz
    ) {
      return true;
    }
  }, [sourceCurrencies, order, selectedAccountOrClient, isExchangeEnabled, getIsValidOrder]);

  const cost = useLoadableState<CalculatedCostState>(async () => {
    if (isExchangeEnabled) {
      return Loadable.uninitialized;
    }
    const validatedOrder = getIsValidOrder(localOrder);
    if (!inventoryDetails.isLoaded || !validatedOrder) {
      return Loadable.uninitialized;
    }

    const cost: LoadableState<CalculatedCostState> = await calculateOrderCost(
      validatedOrder.instrument,
      validatedOrder,
    );
    return cost;
  }, [localOrder, inventoryDetails, calculateOrderCost, getIsValidOrder]);

  const hasMissingValue = useMemo(() => {
    if (cost.isLoaded && initialCalculatedCost.isLoaded && availableBalance.isLoaded) {
      const investableAmount = Math.max(availableBalance.value, 0);
      const missingValue = Math.max(cost.totalPriceBuy - investableAmount, 0);
      if (initialOrderPrice) {
        const missingValueWhenOrderModified = Math.max(
          missingValue - initialOrderPrice - initialCalculatedCost.value,
          0,
        );

        return missingValueWhenOrderModified > 0;
      }

      return missingValue > 0;
    }

    return false;
  }, [cost, availableBalance, initialCalculatedCost, initialOrderPrice]);

  // useLoadableState don't work well here
  const loadBaseInfo = useCallback(async () => {
    setBaseInfo(Loadable.loading);
    if (!isExchangeEnabled) {
      return setBaseInfo(Loadable.uninitialized);
    }
    const validatedOrder = getIsValidOrder(localOrder);
    if (!inventoryDetails.isLoaded || !validatedOrder) {
      return setBaseInfo(Loadable.failed);
    }

    const cost: LoadableState<CalculatedCostState> = await calculateOrderCost(
      validatedOrder.instrument,
      validatedOrder,
    );
    if (cost.isLoaded && initialCalculatedCost.isLoaded) {
      const cashInfo = getCashInfoByCurrency(selectedAccountOrClient);
      if (cashInfo.isLoading) {
        return setBaseInfo(Loadable.loading);
      } else if (cashInfo.isLoaded && availableBalance.isLoaded) {
        const currency = validatedOrder.instrument.currency as Currency;
        // when thre currency is not in the cashInfo, we need to filler it with 0
        const val: CashInfo = cashInfo.cashInfo[currency] ?? {
          investable: 0,
          inventoryHuf: 0,
        };

        const investableAmount = BigNumber.max(availableBalance.value, 0);

        if (isDefined(val)) {
          // invastable value can't be negative, so we need to make sure it is not
          const missingValue = BigNumber.max(new BigNumber(cost.totalPriceBuy).minus(investableAmount), 0);

          // if initialOrderPrice is defined, it means we are modifying an existing order
          if (initialOrderPrice) {
            const missingValueWhenOrderModified = BigNumber.max(
              missingValue.minus(initialOrderPrice).minus(initialCalculatedCost.value),
              0,
            );

            return setBaseInfo(
              Loadable.loaded({
                transactionValueGrossRaw: cost,
                transactionValue: new BigNumber(validatedOrder.price!).times(validatedOrder.quantity).toNumber(),
                transactionValueGross: cost.totalPriceBuy,
                availabeBalance: investableAmount.plus(initialOrderPrice).plus(initialCalculatedCost.value).toNumber(),
                missingValue: missingValueWhenOrderModified.toNumber(),
              }),
            );
          } else {
            return setBaseInfo(
              Loadable.loaded({
                transactionValueGrossRaw: cost,
                transactionValue: new BigNumber(validatedOrder.price!).times(validatedOrder.quantity).toNumber(),
                transactionValueGross: cost.totalPriceBuy,
                availabeBalance: investableAmount.toNumber(),
                missingValue: missingValue.toNumber(),
              }),
            );
          }
        }
      }

      return setBaseInfo(Loadable.failed);
    } else if (cost.hasError) {
      return setBaseInfo(Loadable.failed);
    } else {
      return setBaseInfo(Loadable.loading);
    }
  }, [
    localOrder,
    selectedAccountOrClient,
    inventoryDetails,
    initialOrderPrice,
    availableBalance,
    isExchangeEnabled,
    setBaseInfo,
    calculateOrderCost,
    getCashInfoByCurrency,
    initialCalculatedCost,
    getIsValidOrder,
  ]);

  useEffect(() => {
    loadBaseInfo();
  }, [loadBaseInfo]);

  useEffect(() => {
    if (baseInfo.isLoaded) {
      if (baseInfo.missingValue === 0 && !isFullTransactionChange) {
        setSourceAmount(0);
      }

      if (initialOrderPrice && initialCalculatedCost.isLoaded && isFullTransactionChange) {
        setTargetAmount(
          new BigNumber(baseInfo.transactionValueGross)
            .minus(initialOrderPrice)
            .minus(initialCalculatedCost.value)
            .toNumber(),
        );
      } else {
        setTargetAmount(isFullTransactionChange ? baseInfo.transactionValueGross : baseInfo.missingValue);
      }
    } else {
      setTargetAmount(undefined);
      setSourceAmount(undefined);
    }
  }, [baseInfo, isFullTransactionChange, setSourceAmount, setTargetAmount, initialOrderPrice, initialCalculatedCost]);

  const setExchangeDisabled = useCallback(() => {
    updateOrder(undefined);
    setExchangeRate(Loadable.uninitialized);
    setSelectedSource(null);
    setIsExchangeEnabled(false);
    setIsFullTransactionChange(false);
  }, [updateOrder, setExchangeRate, setSelectedSource, setIsExchangeEnabled]);

  useEffect(() => {
    if (selectedAccountOrClient.client || selectedAccountOrClient.account?.accountCategory === AccountCategory.Nyesz) {
      setExchangeDisabled();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- prevent infinity cycle
  }, [selectedAccountOrClient]);

  const updateOrderExchange = useCallback(
    (order?: OrderFormData) => {
      const isInitial = !isDefined(localOrder);
      const isOrderInstrumentChanged = localOrder?.instrument?.ric !== order?.instrument?.ric;

      const isOrderChanged = isOrderInstrumentChanged || !deepEqual(localOrder, order, ['currencyExchangeParams']);

      // detect change and cache the order
      if (isOrderChanged) {
        setLocalOrder(order);
        removeValidationError(OrderExchangeValidationError.NotEnoughCurrencyBalanceToFullOrder);

        // We need to close the exchange if these fields are invalid in the order. (Only when the order is changed)
        if (
          order?.tradeType === TradeType.Sell ||
          order?.orderType === OrderType.Market ||
          order?.orderType === OrderType.StopMarket
        ) {
          setExchangeDisabled();
        }
      }

      const validatedOrder = getIsValidOrder(order);

      if (validatedOrder) {
        if (isOrderInstrumentChanged) {
          if (
            isInitial &&
            isDefined(validatedOrder.currencyExchangeParams) &&
            validatedOrder.currencyExchangeParams !== 'initialized'
          ) {
            setSelectedSource(validatedOrder.currencyExchangeParams.sourceCurrency);
            setTargetAmount(baseInfo.isLoaded ? baseInfo.missingValue : undefined);
          } else {
            setSelectedSource(null);
            setCurrencyExchangeBalance(Loadable.uninitialized);
            setExchangeRate(Loadable.uninitialized);
            setExchangeSettlementDate(Loadable.uninitialized);
            setTargetAmount(baseInfo.isLoaded ? baseInfo.missingValue : undefined);
          }
        }
      } else if (!validatedOrder) {
        if (sourceCurrencies.isLoading || sourceCurrencies.isLoaded || sourceCurrencies.hasError) {
          setTargetAmount(0);
          setSourceAmount(0);
          setExchangeRate(Loadable.uninitialized);
        }
      }
    },
    [
      removeValidationError,
      setCurrencyExchangeBalance,
      setExchangeRate,
      setExchangeSettlementDate,
      setSelectedSource,
      setTargetAmount,
      setExchangeDisabled,
      sourceCurrencies,
      baseInfo,
      localOrder,
      setSourceAmount,
      getIsValidOrder,
    ],
  );

  const handleOrderChange = useCallback(
    (order?: OrderFormData) => {
      debounce(async () => {
        updateOrderExchange(order);
      });
    },
    [debounce, updateOrderExchange],
  );

  useEffect(() => {
    if (!isDefined(selectedAccountNumber)) {
      resetLoadables();
      return;
    }

    if (localOrder?.instrument?.currency && inventoryDetails.isLoaded) {
      loadSourceCurrencies({ targetCurrency: localOrder?.instrument?.currency, force: true }).then(() => {
        startRefreshCurrencies();
      });
      return () => stopRefreshCurrencies();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps --  we want to run this effect only when currency or selectedAccountOrClient changed
  }, [localOrder?.instrument?.currency, inventoryDetails, selectedAccountNumber]);

  const checkCurrencyBalanceAmount = useCallback(() => {
    if (baseInfo.isLoaded && selectedSource && targetAmount) {
      if (currencyExchangeBalance.isLoaded && isDefined(currencyExchangeBalance.investableAmount) && sourceAmount) {
        if (currencyExchangeBalance.investableAmount < sourceAmount) {
          if (isFullTransactionChange) {
            return pushValidationError(OrderExchangeValidationError.NotEnoughCurrencyBalanceToFullOrder);
          } else {
            return pushValidationError(OrderExchangeValidationError.NotEnoughCurrencyBalance);
          }
        }
      }
    }

    removeValidationError(OrderExchangeValidationError.NotEnoughCurrencyBalance);
    removeValidationError(OrderExchangeValidationError.NotEnoughCurrencyBalanceToFullOrder);
  }, [
    isFullTransactionChange,
    baseInfo.isLoaded,
    currencyExchangeBalance,
    pushValidationError,
    removeValidationError,
    selectedSource,
    sourceAmount,
    targetAmount,
  ]);

  useEffect(() => {
    checkCurrencyBalanceAmount();
  }, [checkCurrencyBalanceAmount]);

  const onTransactionValueChange = useCallback(async () => {
    startTransition(async () => {
      if (isFullTransactionChange) {
        setIsFullTransactionChange(false);
      } else if (
        baseInfo.isLoaded &&
        selectedSource &&
        localOrder?.instrument?.currency &&
        currencyExchangeBalance.isLoaded &&
        currencyExchangeBalance.investableAmount
      ) {
        const exchangeRateForOrder = await loadExchangeRateForOrder(
          selectedSource,
          localOrder?.instrument?.currency,
          baseInfo.transactionValueGross,
        );
        const correctedExchangeRate = getCorrectExchangeRate({
          currencyPair: exchangeRateForOrder.currencyPair,
          exchangeRate: exchangeRateForOrder.exchangeRate,
          sourceCurrency: selectedSource,
        });
        if (baseInfo.transactionValueGross * correctedExchangeRate > currencyExchangeBalance.investableAmount) {
          pushValidationError(OrderExchangeValidationError.NotEnoughCurrencyBalanceToFullOrder);
        } else {
          setIsFullTransactionChange(true);
        }
      }
    });
  }, [
    baseInfo,
    selectedSource,
    localOrder?.instrument?.currency,
    currencyExchangeBalance,
    loadExchangeRateForOrder,
    pushValidationError,
    startTransition,
    isFullTransactionChange,
  ]);

  const formattedExchangeRate = useMemo(() => {
    if (!exchangeRate.isLoaded || !order?.instrument) {
      return '-';
    }
    const selectedTarget = order.instrument.currency;
    const currencyCross = `${selectedSource}${selectedTarget}`;
    const dominantCurrency = CurrencyDominance[currencyCross];
    if (dominantCurrency === selectedSource) {
      return `1 ${selectedTarget} = ${formatNumber(exchangeRate.exchangeRate)} ${selectedSource}`;
    } else if (dominantCurrency === selectedTarget) {
      return `1 ${selectedSource} = ${formatNumber(exchangeRate.exchangeRate)} ${selectedTarget}`;
    }

    return '-';
  }, [exchangeRate, order, selectedSource, formatNumber]);

  const refreshSourceAmount = useCallback(
    async (selectedAccountOrClient: SelectedAccountOrClient) => {
      const validatedOrder = getIsValidOrder(localOrder);

      if (
        !validatedOrder ||
        !selectedSource ||
        !targetAmount ||
        selectedAccountOrClient.client ||
        selectedAccountOrClient.account?.accountCategory === AccountCategory.Nyesz
      ) {
        setExchangeRate(Loadable.uninitialized);
        setSourceAmount(undefined);
        if (selectedSource) {
          updateOrder({
            sourceAmount: undefined,
            targetAmount: targetAmount || 0,
            exchangeRate: undefined,
            sourceCurrency: selectedSource,
          });
        }
        return;
      }

      const res = await getExchangeRateForOrder(selectedSource, validatedOrder.instrument.currency, targetAmount);
      if (res.isLoaded) {
        const correctExchangeRate = getCorrectExchangeRate({
          currencyPair: res.currencyPair,
          exchangeRate: res.exchangeRate,
          sourceCurrency: selectedSource,
        });
        const sourceAmount = Math.ceil(targetAmount * correctExchangeRate);
        setSourceAmount(sourceAmount);
        updateOrder({
          sourceAmount,
          targetAmount,
          exchangeRate: res.exchangeRate,
          sourceCurrency: selectedSource,
        });
      } else {
        setSourceAmount(undefined);
        updateOrder({
          sourceAmount: undefined,
          targetAmount,
          exchangeRate: undefined,
          sourceCurrency: selectedSource,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateOrder is not a dep
    [
      getExchangeRateForOrder,
      setSourceAmount,
      setExchangeRate,
      selectedSource,
      targetAmount,
      localOrder?.instrument,
      getCorrectExchangeRate,
    ],
  );

  // this is responsible for updating the exchange rate
  // it has to be in a useEffect, because it depends on the missingValue loadable state, which is
  // calculated in this context
  useEffect(() => {
    exchangeRateDebounce(() => refreshSourceAmount(selectedAccountOrClient));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshSourceAmount is triggering the effect constantly
  }, [refreshSourceAmount, selectedAccountOrClient, exchangeRateDebounce]);

  // this is responsible for resetting or initializing the order exchange
  useEffect(() => {
    return handleOrderChange(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initOrderExchange is not a dep
  }, [order]);

  const handleIsExchangeEnabledClick = useCallback(() => {
    if (isExchangeEnabled) {
      setExchangeDisabled();
    } else {
      setIsExchangeEnabled(true);
    }
  }, [isExchangeEnabled, setExchangeDisabled, setIsExchangeEnabled]);

  if (!displayOrderExchange) {
    return <></>;
  }

  const isTechnicalError = isExchangeEnabled && (sourceCurrencies.hasError || exchangeRate.hasError);
  const isNoExchangeTime =
    isExchangeEnabled &&
    sourceCurrencies.isLoaded &&
    !sourceCurrencies.hasError &&
    !exchangeRate.hasError &&
    sourceCurrencies.length === 0;

  return (
    <div className={cssWhen(classes.exchange)}>
      {hasMissingValue && !isExchangeEnabled && (
        <FeedbackBox
          type="info"
          message={translateWithParams(translation.orderModal.currencyExchange.missingValue, {
            currency: localOrder?.instrument?.currency ?? '',
          })}
        />
      )}
      <div className={classes.exchangeSwitch}>
        <NbSwitch
          size={'small'}
          color={'secondary'}
          checked={isExchangeEnabled}
          onChange={handleIsExchangeEnabledClick}
        />
        <p
          onClick={handleIsExchangeEnabledClick}
          className={classes.exchangeSwitchTitle}
        >
          {translation.orderModal.exchangeTransaction}
        </p>
      </div>

      {isTechnicalError && (
        <CurrencyExchangeNotAvailableModal
          icon={<NoCurrencyExchangeIcon />}
          title={translation.currencyChange.note.error}
          text={translation.currencyChange.note.tryLater}
          isModal={false}
          onCancel={() => {}}
        >
          <p className={classes.noExchangeTime}>{translation.currencyChange.note.withNightExchangePeriod}</p>
        </CurrencyExchangeNotAvailableModal>
      )}

      {isNoExchangeTime && (
        <CurrencyExchangeNotAvailableModal
          icon={<NoCurrencyExchangeIcon />}
          title={translation.currencyChange.note.currentlyNoExchange}
          text={translation.currencyChange.note.tryLater}
          isModal={false}
          onCancel={() => {}}
        >
          <p className={classes.noExchangeTime}>{translation.currencyChange.note.withNightExchangePeriod}</p>
        </CurrencyExchangeNotAvailableModal>
      )}

      {isExchangeEnabled &&
        order &&
        order.instrument &&
        !sourceCurrencies.hasError &&
        !exchangeRate.hasError &&
        ((sourceCurrencies.isLoaded && sourceCurrencies.length > 0) || sourceCurrencies.isLoading) && (
          <div className={classes.exchangeContainer}>
            <div className={classes.exchangeRows}>
              <div className={classes.exchangeRow}>
                <p>{translation.orderModal.exchangeDetails.transactionValue}</p>
                <CurrencyWhenLoaded
                  value={baseInfo}
                  getValue={val => val.transactionValue}
                  currency={order.instrument.currency}
                  formatCurrency={formatCurrency}
                />
              </div>

              <div className={classes.exchangeRow}>
                <p>{translation.orderModal.exchangeDetails.commission}</p>
                <CurrencyWhenLoaded
                  value={baseInfo}
                  getValue={val =>
                    val.transactionValueGrossRaw.isLoaded
                      ? (val.transactionValueGrossRaw.calculatedCost as CalculatedCostSell).buyAmount
                      : 0
                  }
                  currency={order.instrument.currency}
                  formatCurrency={formatCurrency}
                />
              </div>

              <div className={classes.exchangeRow}>
                <p>{translation.orderModal.exchangeDetails.availableBalance}</p>
                <CurrencyWhenLoaded
                  value={baseInfo}
                  currency={order.instrument.currency}
                  getValue={val => val.availabeBalance}
                  formatCurrency={formatCurrency}
                />
              </div>

              <div className={cssWhen(classes.exchangeRow, classes.exchangeRowTextIsBold)}>
                <p>{translation.orderModal.exchangeDetails.missingValue}</p>
                <CurrencyWhenLoaded
                  value={baseInfo}
                  currency={order.instrument.currency}
                  getValue={val => val.missingValue}
                  formatCurrency={formatCurrency}
                />
              </div>
            </div>

            <OrderCurrencyExchange.SourceAndTargetCurrencies order={order} />

            {selectedSource && (
              <Fade in={!!selectedSource}>
                <div>
                  <OrderCurrencyExchange.SourceAndTargetAmount
                    order={order}
                    transactionValueGross={
                      baseInfo.isLoaded
                        ? baseInfo.transactionValueGrossRaw
                        : (baseInfo as LoadableState<CalculatedCostState>)
                    }
                  />
                </div>
              </Fade>
            )}

            {selectedSource && (
              <>
                {!exchangeRate.isLoaded && !exchangeRate.isLoading && (
                  <div className={classes.exchangeRow}>
                    <p className={classes.exchangeLabel}> </p>
                    <NbButton
                      disabled={isPending}
                      onClick={onTransactionValueChange}
                      variant={'text'}
                    >
                      {isFullTransactionChange
                        ? translation.currencyChange.changeMissingAmount
                        : translation.currencyChange.changeAllAvailableAmount}
                    </NbButton>
                  </div>
                )}
                {exchangeRate.isLoaded && (
                  <div className={classes.exchangeRow}>
                    <p className={classes.exchangeLabel}>
                      {translation.currencyChange.exchangeRate}{' '}
                      <span className={classes.exchangeLabelIsBig}>{formattedExchangeRate}</span>
                    </p>

                    {(isFullTransactionChange ||
                      // check if full transaction button unnecessary
                      (!isFullTransactionChange &&
                        baseInfo.isLoaded &&
                        baseInfo.missingValue !== baseInfo.transactionValueGross)) && (
                      <NbButton
                        onClick={onTransactionValueChange}
                        variant={'text'}
                      >
                        {isFullTransactionChange
                          ? translation.currencyChange.changeMissingAmount
                          : translation.currencyChange.changeAllAvailableAmount}
                      </NbButton>
                    )}
                  </div>
                )}
                {exchangeRate.isLoading && (
                  <div className={classes.exchangeRow}>
                    <div className={classes.exchangeLabel}>
                      {translation.currencyChange.exchangeRate}{' '}
                      <NbIndefiniteLoader
                        className={classes.exchangeLoader}
                        isContrast={true}
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {hasError(OrderExchangeValidationError.NotEnoughCurrencyBalance) && (
              <FeedbackBox
                type="error"
                message={translation.orderModal.currencyExchange.notEnoughCurrencyBalance}
              />
            )}

            {hasError(OrderExchangeValidationError.NotEnoughCurrencyBalanceToFullOrder) && (
              <FeedbackBox
                type="error"
                message={translation.orderModal.currencyExchange.notEnoughCurrencyBalanceToFullOrder}
              />
            )}

            {selectedSource && exchangeRate.isLoaded && (
              <>
                {exchangeSettlementDate.isLoaded && (
                  <div className={classes.exchangeRow}>
                    <p className={classes.exchangeLabel}>
                      {translation.currencyChange.settlementDay}{' '}
                      <span className={classes.exchangeSettlement}>
                        {formatDate(new Date(exchangeSettlementDate.settlementDate))}
                      </span>
                    </p>
                  </div>
                )}
                {exchangeSettlementDate.isLoading && (
                  <div className={classes.exchangeRow}>
                    <div className={classes.exchangeLabel}>
                      {translation.currencyChange.settlementDay}{' '}
                      <NbIndefiniteLoader
                        className={classes.exchangeLoader}
                        isContrast={true}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
    </div>
  );
};
